import { useEffect, useState } from "react";
import { Page, Progress, NavButtons, BigTextInput, BigTextArea, ConfirmScreen, StepTitle } from "@/components/wizard";
import { postAppointment } from "@/lib/api";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\+?[0-9][0-9\s\-./()]{5,19}$/;

const Choice = ({ options, value, onChange, testPrefix }) => (
  <div className="mt-8 flex flex-col gap-3">
    {options.map((opt) => (
      <button
        key={opt}
        type="button"
        data-testid={`${testPrefix}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        onClick={() => onChange(opt)}
        className={`w-full min-h-[56px] px-5 rounded-xl text-base font-medium transition-[border-color,box-shadow,transform] ${
          value === opt
            ? "bg-[#F4F6F8] border-2 border-[#142D43] text-[#142D43] font-semibold shadow-[0_3px_10px_rgba(20,45,67,0.08)] active:translate-y-[1px]"
            : "bg-white border border-[#E5E7EB] text-[#142D43] hover:border-[rgba(20,45,67,0.25)] hover:bg-[#F4F6F8] shadow-[0_3px_10px_rgba(20,45,67,0.05)] active:translate-y-[1px]"
        }`}
      >
        {opt}
      </button>
    ))}
  </div>
);

export default function Appointment() {
  const [idx, setIdx] = useState(0);
  const [vals, setVals] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [hp, setHp] = useState("");
  const [done, setDone] = useState(false);
  const [idem] = useState(() => crypto.randomUUID());

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [idx, done]);

  const set = (k) => (v) => setVals((s) => ({ ...s, [k]: v }));

  const steps = [
    { id: "name", title: "Nome e cognome" },
    { id: "birth_date", title: "Data di nascita" },
    { id: "modality", title: "Come preferisci svolgere l'appuntamento?" },
    { id: "phone", title: "Numero di telefono" },
    { id: "email", title: "Email" },
    { id: "type", title: "Che tipo di appuntamento richiedi?" },
    { id: "reason", title: "Qual è il motivo della richiesta?" },
    { id: "summary", title: "Riepilogo" },
  ];
  const step = steps[idx];

  const canContinue = () => {
    switch (step.id) {
      case "name": return (vals.name || "").trim().length >= 2;
      case "birth_date": return !!(vals.birth_date || "").trim();
      case "modality": return !!vals.modality;
      case "phone": return PHONE_RE.test((vals.phone || "").trim());
      case "email": return EMAIL_RE.test((vals.email || "").trim());
      case "type": return !!vals.type;
      case "reason": return (vals.reason || "").trim().length >= 2;
      default: return true;
    }
  };

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      await postAppointment({
        idempotency_key: idem,
        name: vals.name.trim(),
        birth_date: vals.birth_date,
        modality: vals.modality,
        phone: vals.phone.trim(),
        email: vals.email.trim(),
        appointment_type: vals.type,
        reason: vals.reason.trim(),
        website: hp,
      });
      setDone(true);
    } catch (e) {
      setError("Invio non riuscito. Controlla la connessione e riprova.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <ConfirmScreen
        title="Richiesta inviata"
        text="Lo studio ha ricevuto la tua richiesta di appuntamento."
        note="La richiesta non costituisce una conferma dell'appuntamento: lo studio ti ricontatterà."
      />
    );
  }

  return (
    <Page narrow>
      <Progress current={idx + 1} total={steps.length} />
      <div data-testid={`appointment-step-${step.id}`}>
        <StepTitle title={step.title} />

        {step.id === "name" && (
          <BigTextInput value={vals.name || ""} onChange={set("name")} placeholder="Mario Rossi" testId="appointment-name-input" autoFocus />
        )}
        {step.id === "birth_date" && (
          <BigTextInput value={vals.birth_date || ""} onChange={set("birth_date")} testId="appointment-birthdate-input" type="date" />
        )}
        {step.id === "modality" && (
          <Choice options={["IN PRESENZA", "ONLINE"]} value={vals.modality || ""} onChange={set("modality")} testPrefix="modality" />
        )}
        {step.id === "phone" && (
          <BigTextInput value={vals.phone || ""} onChange={set("phone")} placeholder="+41 79 000 00 00" testId="appointment-phone-input" type="tel" />
        )}
        {step.id === "email" && (
          <BigTextInput value={vals.email || ""} onChange={set("email")} placeholder="nome@email.com" testId="appointment-email-input" type="email" />
        )}
        {step.id === "type" && (
          <Choice options={["VISITA PSICHIATRICA", "PSICOTERAPIA"]} value={vals.type || ""} onChange={set("type")} testPrefix="apptype" />
        )}
        {step.id === "reason" && (
          <BigTextArea value={vals.reason || ""} onChange={set("reason")} placeholder="Descrivi brevemente il motivo della richiesta..." testId="appointment-reason-input" />
        )}
        {step.id === "summary" && (
          <div className="mt-8 border border-[#E5E7EB] rounded-xl divide-y divide-[#E5E7EB]" data-testid="appointment-summary">
            {[
              ["Nome e cognome", vals.name, "summary-name"],
              ["Data di nascita", vals.birth_date, "summary-birthdate"],
              ["Modalità", vals.modality, "summary-modality"],
              ["Telefono", vals.phone, "summary-phone"],
              ["Email", vals.email, "summary-email"],
              ["Tipo di appuntamento", vals.type, "summary-type"],
              ["Motivo", vals.reason, "summary-reason"],
            ].map(([label, value, tid]) => (
              <div key={tid} className="px-5 py-4" data-testid={tid}>
                <p className="text-xs text-[#374151] uppercase tracking-wide">{label}</p>
                <p className="mt-1 text-sm sm:text-base text-[#142D43] leading-relaxed">{value}</p>
              </div>
            ))}
          </div>
        )}

        {error ? <p className="mt-4 text-sm text-red-700" data-testid="appointment-error">{error}</p> : null}

        <input
          type="text"
          value={hp}
          onChange={(e) => setHp(e.target.value)}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          className="absolute left-[-9999px] opacity-0 h-0 w-0"
        />

        <NavButtons
          onBack={() => setIdx(Math.max(0, idx - 1))}
          onNext={() => (step.id === "summary" ? submit() : setIdx(idx + 1))}
          canNext={canContinue()}
          nextLabel={step.id === "summary" ? "INVIA RICHIESTA" : "CONTINUA"}
          nextTestId={step.id === "summary" ? "appointment-form-submit" : "wizard-next-button"}
          showBack={idx > 0}
          loading={submitting}
        />
      </div>
    </Page>
  );
}
